import { useMemo, useState } from 'react'
import { Flex } from '@mantine/core'
import { useQuery } from '@tanstack/react-query'
import type { Dayjs } from 'dayjs'
import dayjs from 'dayjs'

import { ShadowBox } from '@/components/@Shared/ShadowBox/ShadowBox'
import { Label } from '@/components/Label/Label'
import { Loader } from '@/components/Loader/Loader'
import { MonthPicker } from '@/components/MonthPicker/MonthPicker'
import { Title } from '@/components/Title/Title'

import * as Common from '@/__generated__/queries/common'
import {
  AdvisorsService,
  BankingService,
  type GetSpendingStatsData,
} from '@/__generated__/requests'

import { formatMoney } from '@/helpers/formatMoney.helper'

import SpendingIcon from '@/assets/icons/spending.svg?react'

export function SpendingSummary({ userId }: { userId?: number }) {
  const now = dayjs()
  const isUser = !!userId

  const [startDate, setStartDate] = useState<string>(
    now.startOf('month').toISOString(),
  )

  const [endDate, setEndDate] = useState<string>(
    now.endOf('month').toISOString(),
  )

  const queryFn = isUser
    ? () =>
        AdvisorsService.advisorUsersControllerGetSpendingStats({
          endDate,
          startDate,
          userId: +userId,
        })
    : () =>
        BankingService.bankingControllerGetSpendingStats({
          endDate,
          startDate,
        })

  const queryKey = isUser
    ? Common.UseAdvisorsServiceAdvisorUsersControllerGetSpendingStatsKeyFn({
        endDate,
        startDate,
        userId: +userId,
      })
    : Common.UseBankingServiceBankingControllerGetSpendingStatsKeyFn({
        endDate,
        startDate,
      })

  const { data, isFetching, isLoading } = useQuery({
    queryFn,
    queryKey,
    staleTime: 60 * 60 * 1000,
  })

  const spendingData = useMemo<GetSpendingStatsData>(
    () =>
      data ?? {
        count: 0,
        value: 0,
      },
    [data],
  )

  const handleMonthChange = (value: Dayjs) => {
    setEndDate(value.endOf('month').toISOString())
    setStartDate(value.startOf('month').toISOString())
  }

  return (
    <ShadowBox>
      <Flex direction="column" h={120} justify="space-between">
        <Flex align="center" justify="space-between">
          <Flex align="center">
            <SpendingIcon />

            <Title mb={0} ml={10} variant="h2">
              Spending
            </Title>
          </Flex>

          <MonthPicker month={now} onChange={handleMonthChange} />
        </Flex>

        <Flex
          align={{
            base: 'flex-start',
            lg: 'flex-start',
            sm: 'center',
            xl: 'center',
          }}
          direction={{ base: 'column', lg: 'column', sm: 'row', xl: 'row' }}
          justify="space-between"
        >
          {isFetching || isLoading ? (
            <Loader isMinimized />
          ) : (
            <>
              <Title variant="h1_big">
                {formatMoney(spendingData.value, 'USD')}
              </Title>

              <Label
                label={`Transactions <strong>${spendingData.count}</strong>`}
                my={8}
              />
            </>
          )}
        </Flex>
      </Flex>
    </ShadowBox>
  )
}
